import type { OpenPostPostingWrapper } from '@festina/life-general-types/life'
import { notEmpty, typeUtils } from '@festina-life/core'
import { useEffect, useState } from 'react'

import { useHook } from '../../lenteHooks'

type FetchedOpenPostPostings = {
  postingWrappers?: OpenPostPostingWrapper[]
  isLoading: boolean
  error: Error | null
}

export const useLazyFetchLifeOpenPostPostingsForWorkPadIds = (
  workPadIds: string[],
): FetchedOpenPostPostings => {
  const { tenantId, ownerId } = useHook('routing.general.useEntityParams')()
  const { mutateAsync: fetchOpenPostPostings } = useHook(
    'lazyFetchers.life.useLazyFetchOpenPostPostings',
  )()

  const [postingWrappers, setPostingWrappers] = useState<
    OpenPostPostingWrapper[] | 'loading' | Error
  >('loading')

  useEffect(() => {
    const fetchData = async () => {
      try {
        const openPostPostingWrapperPromises = workPadIds.map((workPadId) =>
          fetchOpenPostPostings({
            tenantId,
            workPadId,
            key1: ownerId,
          }),
        )

        const openPostPostingCollections = await Promise.all(
          openPostPostingWrapperPromises,
        )

        const openPostPostingWrappers = openPostPostingCollections
          ?.flatMap((collection) => collection?.openPostPostingWrappers ?? [])
          .filter(notEmpty)

        setPostingWrappers(openPostPostingWrappers)
      } catch (error) {
        setPostingWrappers(error)
      }
    }

    void fetchData()
  }, [fetchOpenPostPostings, ownerId, tenantId, workPadIds])

  if (typeUtils.isLoading(postingWrappers)) {
    return {
      isLoading: true,
      error: null,
    }
  }

  if (typeUtils.isError(postingWrappers)) {
    return {
      isLoading: false,
      error: postingWrappers,
    }
  }

  return {
    postingWrappers,
    isLoading: false,
    error: null,
  }
}
